"use client";

import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FooterContactFormData, FooterContactFormSchema } from '@/lib/types';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { submitContactForm } from '@/lib/actions/contact-form-actions';
import { cn } from '@/lib/utils';

interface ContactFormProps {
  contactToEmail: string;
  title?: string; 
  titleClassName?: string; // Nova prop para classes do título
}

export function MiniSiteContactForm({ 
  contactToEmail, 
  title = "Fale Conosco", 
  titleClassName 
}: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FooterContactFormData>({
    resolver: zodResolver(FooterContactFormSchema),
  });

  const onSubmit: SubmitHandler<FooterContactFormData> = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await submitContactForm(data, contactToEmail);
      if (result.success) {
        toast({ title: "Sucesso!", description: result.message });
        reset(); 
      } else {
        toast({ title: "Erro", description: result.message, variant: "destructive" });
      }
    } catch (error) {
      console.error("Erro ao submeter formulário:", error);
      toast({ title: "Erro Inesperado", description: "Ocorreu um problema ao enviar sua mensagem.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <h4 className={cn("font-headline text-lg font-semibold mb-3", titleClassName || "text-gray-800")}>{title}</h4>
      {/* Subtítulo removido */}
      {/* Honeypot field for spam protection */}
      <div className="absolute opacity-0 h-0 w-0">
        <label htmlFor="honeypot">Não preencha este campo</label>
        <input id="honeypot" tabIndex={-1} autoComplete="off" {...register("honeypot")} suppressHydrationWarning />
      </div>
      <div>
        <Input 
          placeholder="Seu Nome" 
          {...register("name")}
          aria-invalid={errors.name ? "true" : "false"}
          disabled={isSubmitting}
          suppressHydrationWarning={true}
        />
        {errors.name && <p className="text-xs text-destructive mt-1">{errors.name.message}</p>}
      </div>
      <div>
        <Input 
          type="email" 
          placeholder="Seu Melhor E-mail" 
          {...register("email")} 
          aria-invalid={errors.email ? "true" : "false"}
          disabled={isSubmitting}
          suppressHydrationWarning={true}
        />
        {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
      </div>
      <div>
        <Input 
          type="tel" 
          placeholder="Seu Telefone/WhatsApp (Opcional)" 
          {...register("phone")} 
          disabled={isSubmitting}
          suppressHydrationWarning={true}
        />
      </div>
      <div>
        <Input 
          placeholder="Assunto da Mensagem" 
          {...register("subject")} 
          aria-invalid={errors.subject ? "true" : "false"}
          disabled={isSubmitting}
          suppressHydrationWarning={true}
        />
        {errors.subject && <p className="text-xs text-destructive mt-1">{errors.subject.message}</p>}
      </div>
      <div>
        <Textarea 
          placeholder="Sua Mensagem..." 
          rows={4} 
          {...register("message")} 
          aria-invalid={errors.message ? "true" : "false"}
          disabled={isSubmitting}
          suppressHydrationWarning={true}
        />
        {errors.message && <p className="text-xs text-destructive mt-1">{errors.message.message}</p>}
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting} style={{ backgroundColor: 'var(--template-primary-color)'}}>
        {isSubmitting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        Enviar Mensagem
      </Button>
    </form>
  );
}